import { ExclamationTriangleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { CodeDefinition } from '../../../../js/api/models/CodeDefinition';
import type { Contact } from '../../../../js/api/models/Contact';
import type { FacilityContact } from '../../../../js/api/models/FacilityContact';
import { RoleEnum } from '../../../../js/api/models/RoleEnum';
import { HealthcareEntitiesService } from '../../../../js/api/services/HealthcareEntitiesService';
import { handleApiError } from '../../../../js/app/utils/errorhandling';
import { Button } from '../../components/button';
import { Dialog, DialogTitle, DialogBody, DialogActions } from '../../components/dialog';
import { Fieldset, Field, Label } from '../../components/fieldset';
import { Input } from '../../components/input';
import { Navbar, NavbarItem } from '../../components/navbar';
import { useStaffOrgFilter } from '../../hooks/useStaffOrgFilter';
import { reactSelectReset } from '../../utils/utils';

// Validation schema for contact fields
const contactValidationSchema = z.object({
  phoneNumber: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a phone number',
        fatal: false,
      });
      return;
    }
    // Basic phone number validation (allows common formats)
    const phoneRegex = /^(\+\d{1,2}\s?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/;
    if (!phoneRegex.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid phone number',
        fatal: true,
      });
    }
  }),
  email: z.string().superRefine((val, ctx) => {
    if (val.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter an email address',
        fatal: true,
      });
      return;
    }
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(val)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please enter a valid email address',
        fatal: true,
      });
    }
  }),
});

// Validation message component
const ValidationMessage = ({
  message,
  icon,
  colorClass,
}: {
  message?: string;
  icon?: React.ReactNode;
  colorClass?: string;
}) => {
  if (!message) return null;
  return (
    <div className={`tw:mt-1 tw:flex tw:items-center tw:gap-1 tw:text-sm ${colorClass}`}>
      {icon}
      {message}
    </div>
  );
};

// Helper function to create validation messages
const createValidationMessages = (errors: z.ZodError) => {
  const newValidationMessages: Record<
    string,
    { message: string; icon: React.ReactNode; colorClass: string }
  > = {};

  errors.errors.forEach((err) => {
    const field = err.path[0];
    newValidationMessages[field] = {
      message: err.message,
      icon: err.fatal ? (
        <XCircleIcon className="tw:h-5 tw:w-5" />
      ) : (
        <ExclamationTriangleIcon className="tw:h-5 tw:w-5" />
      ),
      colorClass: err.fatal
        ? 'tw:text-stoplight-danger tw:dark:text-red-500'
        : 'tw:text-stoplight-warning tw:dark:text-stoplight-warning',
    };
  });

  return newValidationMessages;
};

interface SelectedContact {
  contact: Contact;
  role: RoleEnum | '';
}

interface AddFacilityContactDialogProps {
  open: boolean;
  onClose: () => void;
  roleOptions: CodeDefinition[];
  mode?: 'add' | 'edit';
  contact?: {
    first_name?: string;
    last_name?: string;
    email?: string;
    phone_number?: string;
    phone_number_extension?: string;
    role?: RoleEnum;
  };
  facilityId?: string;
  /** Org id of the facility being edited — when present, scopes the
   *  available-contacts list to this org, overriding any staff acting-as fallback. */
  healthcareEntityId?: string;
  onSuccess?: (newContacts: FacilityContact[]) => void;
}

type Tab = 'existing' | 'new';

interface ContactListItemProps {
  contact: Contact;
  isSelected: boolean;
  selectedContact?: SelectedContact;
  onSelect: (contact: Contact) => void;
  onRoleChange: (contactId: string, role: RoleEnum | '') => void;
  roleOptions: CodeDefinition[];
}

const ContactListItem: React.FC<ContactListItemProps> = ({
  contact,
  isSelected,
  selectedContact,
  onSelect,
  onRoleChange,
  roleOptions,
}) => {
  // Transform roleOptions to react-select format
  const selectOptions = roleOptions.map((option) => ({
    value: option.code,
    label: option.title,
  }));

  return (
    <div
      className={`tw:flex tw:items-center tw:p-2 tw:transition-colors tw:px-4 tw:mb-0 ${
        isSelected ? 'tw:bg-brand-green-1' : 'tw:hover:bg-brand-green-2'
      }`}
    >
      <button
        type="button"
        className="tw:flex-1 tw:text-left tw:flex tw:flex-col tw:gap-0.5 "
        onClick={() => onSelect(contact)}
      >
        <span
          className={`tw:font-medium ${isSelected ? 'tw:text-white' : 'tw:text-zinc-900 tw:dark:text-zinc-100'}`}
        >
          {contact.first_name} {contact.last_name}
        </span>
        <span
          className={`tw:text-sm ${isSelected ? 'tw:text-white/80' : 'tw:text-zinc-400 tw:dark:text-zinc-400'} tw:max-w-[200px]`}
        >
          {contact.email}
        </span>
      </button>
      <div className="tw:min-w-60">
        <Select
          options={selectOptions}
          value={selectOptions.find((option) => option.value === selectedContact?.role) || null}
          onChange={(option) =>
            contact.id && onRoleChange(contact.id, option?.value as RoleEnum | '')
          }
          placeholder="Select Role"
          className={`tw:w-full ${!isSelected ? 'tw:opacity-0 tw:pointer-events-none' : ''}`}
          isDisabled={!isSelected}
          styles={reactSelectReset}
        />
      </div>
    </div>
  );
};

export const AddFacilityContactDialog: React.FC<AddFacilityContactDialogProps> = ({
  open,
  onClose,
  roleOptions,
  mode = 'add',
  contact,
  facilityId,
  healthcareEntityId,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<Tab>('existing');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');
  const [role, setRole] = useState<RoleEnum | ''>('');
  const [availableContacts, setAvailableContacts] = useState<Contact[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedContacts, setSelectedContacts] = useState<SelectedContact[]>([]);
  const [validationMessages, setValidationMessages] = useState<
    Record<string, { message: string; icon: React.ReactNode; colorClass: string }>
  >({});
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const staffOrgFilter = useStaffOrgFilter();
  // Prefer the facility's own org when known so cross-org staff edits show
  // the right contacts; fall back to the staff acting-as id otherwise.
  const contactsHealthcareEntityFilter = healthcareEntityId || staffOrgFilter;

  const createNewContactMutation = useMutation({
    mutationFn: (data: {
      facility: string;
      contact: {
        first_name: string;
        last_name: string;
        email: string;
        phone_number?: string;
        phone_number_extension?: string;
      };
      role: RoleEnum;
    }) =>
      HealthcareEntitiesService.healthcareEntitiesFacilityContactsCreate({
        requestBody: data,
      }),
    onSuccess: (newContact) => {
      toast.success('Contact added successfully!');
      onSuccess?.([newContact]);
      onClose();
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to add contact' });
    },
  });

  const addExistingContactsMutation = useMutation({
    mutationFn: (
      contacts: Array<{
        facility: string;
        contact: {
          id: string;
          first_name: string;
          last_name: string;
          email: string;
        };
        role: RoleEnum;
      }>
    ) =>
      Promise.all(
        contacts.map((contact) =>
          HealthcareEntitiesService.healthcareEntitiesFacilityContactsCreate({
            requestBody: contact,
          })
        )
      ),
    onSuccess: (newContacts) => {
      toast.success('Contacts added successfully!');
      onSuccess?.(newContacts);
      onClose();
    },
    onError: (error: any) => {
      handleApiError(error, { messagePrefix: 'Failed to add contacts' });
    },
  });

  useEffect(() => {
    const fetchAvailableContacts = async () => {
      if (mode === 'add' && open && facilityId) {
        setIsLoading(true);
        try {
          const response = await HealthcareEntitiesService.healthcareEntitiesContactsList({
            healthcareEntity: contactsHealthcareEntityFilter,
          });
          setAvailableContacts(response.results || []);
        } catch (error) {
          console.error('Failed to fetch available contacts:', error);
        } finally {
          setIsLoading(false);
        }
      }
    };

    fetchAvailableContacts();
  }, [mode, open, facilityId, contactsHealthcareEntityFilter]);

  useEffect(() => {
    if (mode === 'edit' && contact) {
      setFirstName(contact.first_name || '');
      setLastName(contact.last_name || '');
      setEmail(contact.email || '');
      setPhoneNumber(contact.phone_number || '');
      setPhoneExtension(contact.phone_number_extension || '');
      setRole(contact.role || '');
    } else if (mode === 'add' && open) {
      setFirstName('');
      setLastName('');
      setEmail('');
      setPhoneNumber('');
      setPhoneExtension('');
      setRole('');
      setSelectedContacts([]);
    }
  }, [mode, contact, open]);

  // Add validation effect - only run if submission has been attempted
  useEffect(() => {
    if (activeTab === 'new' && hasAttemptedSubmit) {
      const result = contactValidationSchema.safeParse({
        phoneNumber,
        email,
      });

      if (!result.success) {
        setValidationMessages(createValidationMessages(result.error));
      } else {
        setValidationMessages({});
      }
    }
  }, [phoneNumber, email, activeTab, hasAttemptedSubmit]);

  // Reset validation state when switching tabs
  useEffect(() => {
    setValidationMessages({});
    setHasAttemptedSubmit(false);
  }, [activeTab]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setActiveTab('existing');
      setValidationMessages({});
      setHasAttemptedSubmit(false);
      setSearchQuery('');
      setSelectedContacts([]);
    }
  }, [open]);

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (mode === 'edit') {
      // Handle edit mode if needed
      return;
    }

    if (activeTab === 'existing') {
      if (selectedContacts.length === 0) return;

      const missingRole = selectedContacts.some(({ role }) => !role);
      if (missingRole) {
        toast.error('Please select a role for each contact before adding.');
        return;
      }

      const contactsToAdd = selectedContacts
        .filter(
          ({ contact }) => contact.id && contact.first_name && contact.last_name && contact.email
        )
        .map(({ contact, role }) => ({
          facility: facilityId!,
          contact: {
            id: contact.id!,
            first_name: contact.first_name!,
            last_name: contact.last_name!,
            email: contact.email!,
          },
          role: role as RoleEnum,
        }));

      addExistingContactsMutation.mutate(contactsToAdd);
    } else {
      setHasAttemptedSubmit(true);

      // Validate before submission
      const result = contactValidationSchema.safeParse({
        phoneNumber,
        email,
      });

      if (!result.success) {
        // Check if there are any fatal errors
        const hasFatalErrors = result.error.errors.some((err) => err.fatal);
        if (hasFatalErrors) {
          // Update validation messages to show errors
          setValidationMessages(createValidationMessages(result.error));
          toast.error('Please fix the validation errors before submitting.');
          return;
        }
      }

      if (!email || !firstName || !lastName || !role || !facilityId) {
        toast.error('Please fill in all required fields');
        return;
      }

      createNewContactMutation.mutate({
        facility: facilityId,
        contact: {
          first_name: firstName,
          last_name: lastName,
          email,
          phone_number: phoneNumber,
          phone_number_extension: phoneExtension,
        },
        role: role as RoleEnum,
      });
    }
  };

  const handleContactSelect = (contact: Contact) => {
    const isSelected = selectedContacts.some((sc) => sc.contact.id === contact.id);
    if (isSelected) {
      setSelectedContacts(selectedContacts.filter((sc) => sc.contact.id !== contact.id));
    } else {
      setSelectedContacts([...selectedContacts, { contact, role: '' }]);
    }
  };

  const handleRoleChange = (contactId: string, newRole: RoleEnum | '') => {
    setSelectedContacts(
      selectedContacts.map((sc) => (sc.contact.id === contactId ? { ...sc, role: newRole } : sc))
    );
  };

  // Filter contacts by first name, last name, or email
  const filteredContacts = availableContacts.filter((contact) => {
    const query = searchQuery.toLowerCase();
    return (
      (contact.first_name?.toLowerCase() || '').includes(query) ||
      (contact.last_name?.toLowerCase() || '').includes(query) ||
      (contact.email?.toLowerCase() || '').includes(query)
    );
  });

  const renderExistingContactsTab = () => (
    <div className="tw:mt-6">
      <div className="tw:flex-none tw:py-4">
        <input
          type="text"
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="tw:w-full tw:px-3 tw:py-2 tw:border tw:border-zinc-950/10 tw:rounded-lg tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-1 tw:bg-white tw:dark:bg-white/5 tw:dark:border-white/10 tw:dark:text-white/90"
        />
      </div>
      <div className="tw:max-h-96 tw:overflow-y-auto tw:overflow-x-auto">
        <div className="tw:space-y-2 tw:min-w-max">
          {isLoading ? (
            <div className="tw:text-center tw:py-4">Loading contacts...</div>
          ) : filteredContacts.length > 0 ? (
            filteredContacts.map((contact) => {
              const isSelected = selectedContacts.some((sc) => sc.contact.id === contact.id);
              const selectedContact = selectedContacts.find((sc) => sc.contact.id === contact.id);

              return (
                <ContactListItem
                  key={contact.id}
                  contact={contact}
                  isSelected={isSelected}
                  selectedContact={selectedContact}
                  onSelect={handleContactSelect}
                  onRoleChange={handleRoleChange}
                  roleOptions={roleOptions}
                />
              );
            })
          ) : (
            <div className="tw:text-center tw:py-4 tw:text-zinc-400">
              No available contacts found
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderNewContactTab = () => {
    // Transform roleOptions to react-select format
    const selectOptions = roleOptions.map((option) => ({
      value: option.code,
      label: option.title,
    }));

    return (
      <form onSubmit={handleSubmit} className="tw:space-y-6">
        <Fieldset>
          <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4">
            <Field>
              <Label htmlFor="first-name">
                First Name <span className="tw:text-stoplight-danger">*</span>
              </Label>
              <Input
                id="first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="tw:w-full"
                required
              />
            </Field>
            <Field>
              <Label htmlFor="last-name">
                Last Name <span className="tw:text-stoplight-danger">*</span>
              </Label>
              <Input
                id="last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="tw:w-full"
                required
              />
            </Field>
          </div>

          <div className="tw:grid tw:grid-cols-2 tw:gap-4 tw:mb-4">
            <Field>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                className="tw:w-full"
              />
              <ValidationMessage
                message={validationMessages?.phoneNumber?.message}
                icon={validationMessages?.phoneNumber?.icon}
                colorClass={validationMessages?.phoneNumber?.colorClass}
              />
            </Field>
            <Field>
              <Label htmlFor="phone-extension">Phone Extension</Label>
              <Input
                id="phone-extension"
                type="tel"
                value={phoneExtension}
                onChange={(e) => setPhoneExtension(e.target.value)}
                className="tw:w-full"
              />
            </Field>
          </div>

          <div className="tw:grid tw:grid-cols-2 tw:gap-4">
            <Field>
              <Label htmlFor="email">
                Email <span className="tw:text-stoplight-danger">*</span>
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="tw:w-full"
                required
              />
              <ValidationMessage
                message={validationMessages?.email?.message}
                icon={validationMessages?.email?.icon}
                colorClass={validationMessages?.email?.colorClass}
              />
            </Field>
            <Field>
              <Label htmlFor="role">
                Role <span className="tw:text-stoplight-danger">*</span>
              </Label>
              <Select
                options={selectOptions}
                value={selectOptions.find((option) => option.value === role) || null}
                onChange={(option) => setRole(option?.value as RoleEnum)}
                placeholder="Select Role"
                className="tw:w-full"
                styles={reactSelectReset}
              />
            </Field>
          </div>
        </Fieldset>
      </form>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} size="2xl">
      <DialogTitle className="tw:dark:!text-white/90">
        {mode === 'edit' ? 'Edit Facility Contact' : 'Add Facility Contacts'}
      </DialogTitle>
      <DialogBody>
        {mode === 'add' && (
          <Navbar className="tw:mb-6">
            <NavbarItem current={activeTab === 'existing'} onClick={() => setActiveTab('existing')}>
              Add Existing
            </NavbarItem>
            <NavbarItem current={activeTab === 'new'} onClick={() => setActiveTab('new')}>
              Add New
            </NavbarItem>
          </Navbar>
        )}
        {mode === 'edit'
          ? renderNewContactTab()
          : activeTab === 'existing'
            ? renderExistingContactsTab()
            : renderNewContactTab()}
      </DialogBody>
      <DialogActions>
        <Button type="button" plain onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          color="brandgreen0"
          onClick={handleSubmit}
          disabled={
            (mode === 'add' &&
              ((activeTab === 'existing' &&
                (selectedContacts.length === 0 || selectedContacts.some(({ role }) => !role))) ||
                (activeTab === 'new' && (!email || !firstName || !lastName || !role)))) ||
            createNewContactMutation.isLoading ||
            addExistingContactsMutation.isLoading
          }
        >
          {mode === 'edit'
            ? 'Save'
            : activeTab === 'new'
              ? 'Add New Contact'
              : 'Add Selected Contacts'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
